import React, {
  useState, useCallback, useContext, useEffect,
} from 'react';
import clsx from 'clsx';
import { Badge, Icon, Link } from '@printdeal/ui-library';
import { addDataLayerEntry } from '@printdeal/data-layer-manager';
import { debounce } from 'es-toolkit';
import type { Navigation, NavigationItem } from '../../../types';
import NavigationLink from '../NavigationLink';
import DesktopNavigationDropdown from './DesktopNavigationDropdown';
import { HeaderContext } from '../../context';
import {
  dispatchDataLayerEventOnNavigationView,
  dispatchNavigationHotjarEvent,
  addEventLabelForBadges,
} from '../helpers';
import { getBadgeKind } from '../../../../product/selector/helpers/AttributeValueHelper';

type AllCategoriesNavigationProps = {
  navigation: Navigation;
  className?: string;
};

const AllCategoriesNavigation = ({
  navigation,
  className,
}: AllCategoriesNavigationProps) => {
  const {
    isAllCategoriesNavigationOpen,
    setIsAllCategoriesNavigationOpen,
    isSticky,
  } = useContext(HeaderContext);
  const [openItemId, setOpenItemId] = useState<string | undefined>(undefined);

  // Bogus warning, rule is unable to properly work because of the debounce.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleOpen = useCallback(debounce(() => {
    setIsAllCategoriesNavigationOpen(true);

    dispatchNavigationHotjarEvent();

    dispatchDataLayerEventOnNavigationView({
      navType: isSticky ? 'navigation_main_sticky' : 'navigation_main',
      eventLabel: 'categoryLevel1',
    });
    addDataLayerEntry('navigation_main_show');
  }, 200), [isSticky]);

  const handleClose = useCallback(() => {
    // Cancel potential debounced invocation of the open callback.
    handleOpen.cancel();
    // Reset open item ID.
    setOpenItemId(navigation.items?.[0]?.id);
    setIsAllCategoriesNavigationOpen(false);
  }, [navigation.items?.[0]?.id, handleOpen]);

  const handleNavigationItemMouseEnter = useCallback((item: NavigationItem) => {
    setOpenItemId(item.id);
  }, []);

  useEffect(() => {
    // Set first item as open.
    setOpenItemId(navigation.items?.[0]?.id);
  }, [navigation.items?.[0]?.id]);

  return (
    <section
      onMouseEnter={handleOpen}
      onMouseLeave={handleClose}
      className={className}
    >
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <Link
        as="button"
        type="button"
        variant="secondary"
        onFocus={handleOpen}
        className={clsx(
          'pd-hidden lg:pd-inline-flex',
          isSticky ? 'pd-py-8' : 'pd-h-14 pd-border-b-2 pd-pl-4',
          isAllCategoriesNavigationOpen && 'pd-border-primary-500 pd-text-primary-500',
        )}
        // eslint-disable-next-line no-nested-ternary
        iconAfter={isAllCategoriesNavigationOpen ? 'chevron-up' : 'chevron-down'}
      >
        {navigation.title}
      </Link>
      <DesktopNavigationDropdown
        isOpen={isAllCategoriesNavigationOpen}
        setIsOpen={setIsAllCategoriesNavigationOpen}
        data-testid="desktop-navigation-dropdown"
      >
        <div className="pd-min-h-[40rem]">
          <span className="pd-absolute pd-inset-y-0 pd-left-[18.75rem] pd-border-r-1" aria-hidden="true" />
          <ul className="pd-relative pd-flex pd-flex-col pd-gap-3">
            {navigation.items?.map((navigationItem) => (
              <li
                key={navigationItem.id}
                data-level="1"
                className="pd-flex pd-w-72 pd-pr-3"
              >
                <div className="pd-flex pd-w-full pd-gap-6">
                  {/**
                   * Keep chevron right separate from link,
                   * so that users can navigate with cursor from top-level category to sub-categories
                   * more easily without accidentally hovering another top-level category.
                   */}
                  <NavigationLink
                    to={navigationItem.linkedEntry?.path}
                    iconBefore={navigationItem.icon}
                    navigationType="main"
                    navigationLevel="main_Level1"
                    eventLabel={navigationItem.title}
                    onMouseEnter={() => handleNavigationItemMouseEnter(navigationItem)}
                    className={clsx('pd-w-full', openItemId === navigationItem.id && 'pd-text-primary-500')}
                  >
                    {navigationItem.title}
                  </NavigationLink>
                  <Icon icon="chevron-right" />
                </div>
                <div
                  className={clsx(
                    'pd-absolute pd-left-[18.75rem] pd-right-0 pd-top-0',
                    openItemId !== navigationItem.id && 'pd-hidden',
                  )}
                >
                  <ul className="pd-columns-3 pd-gap-6 pd-py-1 pd-pl-6">
                    {navigationItem.collapsibleContent?.map((collapsibleContent) => {
                      if (!collapsibleContent.title) return null;

                      return (
                        <li key={collapsibleContent.id} data-level="2" className="pd-mb-6 pd-flex pd-break-inside-avoid pd-flex-col pd-gap-2">
                          <NavigationLink
                            to={collapsibleContent.linkedEntry?.path}
                            navigationType="main"
                            navigationLevel="level2"
                            eventLabel={collapsibleContent.title}
                          >
                            {collapsibleContent.title}
                          </NavigationLink>
                          {openItemId === navigationItem.id && (
                            <ul className="pd-flex pd-flex-col pd-gap-1">
                              {collapsibleContent.subItems?.map(({
                                linkedEntry, path, title, id, label,
                              }) => {
                                if (!linkedEntry && !path) {
                                  return null;
                                }
                                const navSubItemTitle = linkedEntry ? linkedEntry.title : title;
                                const eventLabelPerBadge = (label && navSubItemTitle)
                                  ? addEventLabelForBadges(
                                    label.type,
                                    navSubItemTitle,
                                    label.displayName,
                                  ) : navSubItemTitle;
                                return (
                                  <li key={id} data-level="3">
                                    <NavigationLink
                                      to={linkedEntry ? linkedEntry.path : path}
                                      navigationType="main"
                                      navigationLevel="level3"
                                      eventLabel={eventLabelPerBadge}
                                      className="pd-mr-2 pd-text-sm"
                                    >
                                      {navSubItemTitle}
                                    </NavigationLink>
                                    {(label) && (
                                      <Badge variant={getBadgeKind(label.type)}>
                                        {label.displayName}
                                      </Badge>
                                    )}
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </DesktopNavigationDropdown>
    </section>
  );
};

export default AllCategoriesNavigation;
